import clsx from 'clsx';
import { Trash2 } from 'lucide-react';
import type { TObjective } from '@hubertai/screening-question-types';
import MutedText from 'components/common/MutedText';
import HubertInput from 'components/HubertUI/HubertInput';
import HubertTextArea from 'components/HubertUI/HubertTextArea';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { removeObjective, updateObjective } from 'rtk/Question.Editor.Slice';
// Shared with the slot-filling editor — same caption/tooltip control, same visual language.
import FieldLabel from '../SlotFilling/FieldLabel';

const ID_INFO = (
  <>
    The <strong>storage key</strong> for this objective&apos;s coverage verdict on the saved answer, and the
    model&apos;s handle on it. A short snake_case name (e.g. <em>team_size</em>) keeps stored verdicts readable. Avoid
    changing it once the question is live — answers already saved reference the old id.
  </>
);

const TEXT_INFO = (
  <>
    What you want to learn, in plain English — this is the whole instruction the model works from, so be concrete about
    what would satisfy you. <em>&quot;How many people they led, and for how long&quot;</em> probes well;{' '}
    <em>&quot;leadership&quot;</em> does not. Never shown to the candidate, so write it in English regardless of the
    interview language.
  </>
);

const REASK_INFO = (
  <>
    Fallback follow-up, shown to the candidate only if the model produces none (e.g. the LLM is unreachable). Normally
    Hubert writes its own follow-up, in the candidate&apos;s language, referencing what they actually said — leave this
    empty unless you need a guaranteed wording.
  </>
);

interface Props {
  objective: TObjective;
  objectiveIndex: number;
}

const Objective = ({ objective, objectiveIndex }: Props) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const language = useAppSelector((state) => selectQuestionLanguage(state, questionId));

  const patch = (value: Partial<TObjective>) =>
    dispatch(updateObjective({ questionId, index: objectiveIndex, patch: value }));
  const title = objective.text.trim() || objective.id.trim();

  return (
    <div className="overflow-hidden rounded-md border">
      <div className={clsx('flex flex-row items-center justify-between gap-3', 'border-b bg-gray-50', 'px-3 py-2')}>
        <div className="flex min-w-0 flex-row items-center gap-2">
          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-600">
            {objectiveIndex + 1}
          </span>
          <span className="truncate text-sm font-medium">{title || <MutedText text="Untitled objective" />}</span>
        </div>

        <Trash2
          size={16}
          className="shrink-0 cursor-pointer text-gray-400 hover:text-gray-700"
          onClick={() => dispatch(removeObjective({ questionId, index: objectiveIndex }))}
        />
      </div>

      <div className="flex flex-col gap-3 p-3">
        <div className="flex flex-col gap-1">
          <FieldLabel label="Objective id" info={ID_INFO} />
          <HubertInput
            className="sm:w-1/3"
            placeholder="team_size"
            value={objective.id}
            onChange={(e) => patch({ id: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel label="What Hubert should learn" hint="model-only, English" info={TEXT_INFO} />
          <HubertTextArea
            rows={2}
            placeholder="e.g. How many people they led, and for how long."
            value={objective.text}
            onChange={(e) => patch({ text: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel label="Fallback follow-up" hint="optional, shown to the candidate" info={REASK_INFO} />
          <HubertTextArea
            rows={2}
            placeholder="e.g. How many people reported to you?"
            value={objective.reask?.[language] ?? ''}
            onChange={(e) => patch({ reask: { ...objective.reask, [language]: e.target.value } })}
          />
        </div>
      </div>
    </div>
  );
};

export default Objective;
